import { fetchLabelById } from "scripts/components/react/api/labels"

/**
 * @typedef {Object} Label
 * @property {number} id - The unique identifier for the label.
 * @property {string} title - The title of the label.
 * @property {string} [description] - An optional description of the label.
 * @property {string} [url] - An optional URL associated with the label.
 * @property {string} [more_detail] - The translation for "More details".
 */

/** @type {Record<number, Promise<Label>>} */
/** Each promise resolves to a Label object */
const labelPromises = {}

/** @type {Record<number, Label>} */
const labelCache = {}

/**
 * Retrieves a label by its ID, caching it for future use to avoid redundant fetches.
 * Prevents race conditions by caching the promise instead of the result.
 * After the first successful fetch, always returns the cached resolved Label.
 * On fetch errors, the promise is rejected and the cache is not set.
 * @param {number} id - The unique identifier of the label.
 * @returns {Promise<Label>} A promise that resolves to the label data object.
 * @throws {Error} If the fetch request fails with a non-OK response.
 */
/**
 * Pre-populate the label cache from catalog API response.
 * Skips entries that are already cached to avoid overwriting fresher data.
 * @param {Label[]} labels
 */
export const seedLabels = (labels) => {
  labels.forEach(label => {
    if (label.id != null && !labelCache[label.id]) {
      labelCache[label.id] = label
    }
  })
}

export const getLabel = async (id) => {
  // Return cached resolved label immediately
  if (labelCache[id]) {
    return labelCache[id]
  }

  // Return existing in-flight promise to prevent race conditions
  if (!labelPromises[id]) {
    labelPromises[id] = (async () => {
      try {
        const label = await fetchLabelById(id)
        labelCache[id] = label
        return label
      } catch (error) {
        // Clean up the promise on error
        delete labelPromises[id]
        throw error
      }
    })()
  }

  return labelPromises[id]
}
